import { useMemo } from 'react';

import { Quote } from '@/model/quotesModel';
import { enrichQuotesWithROI } from '@shared';
import { getInitialInflationLabel, parseInflationPercent } from '@/utils/inflationParser';
import {
  getBenefitPeriodFromQuote,
  benefitPeriodToString,
  stringToBenefitPeriod,
  LIFETIME_PERIOD,
  parseDurationYears as parseBenefitPeriod,
  type BenefitPeriod,
} from '@/utils/benefitPeriod';
import {
  groupQuotesForBenefitDesign,
  reverseInterpolateQuotes,
} from '@/features/shop-insurance/lib/benefit-design-interpolation';
import type {
  BenefitDesignInputs,
  BenefitDesignQuoteGroup,
  CombinedFilterType,
} from '@/features/shop-insurance';

export type InflationOption = { label: string; rate: number };

export interface BenefitDesignAvailableOptions {
  durationOptions: BenefitPeriod[];
  inflationOptions: InflationOption[];
  aobDurationOptions: BenefitPeriod[];
  cobDurationOptions: BenefitPeriod[];
  aobInflationOptions: InflationOption[];
  cobInflationOptions: InflationOption[];
}

interface UseBenefitDesignQuotesParams {
  originalQuotes: Quote[] | undefined;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  inputs: BenefitDesignInputs;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
}

interface UseBenefitDesignQuotesResult {
  quoteGroups: BenefitDesignQuoteGroup[];
  availableOptions: BenefitDesignAvailableOptions;
}

const MAX_DURATION_YEARS = 8;

function matchesTab(quote: Quote, tab: ShopInsuranceTab): boolean {
  if (tab === 'joint') return quote.policyType === 'Joint Life';
  if (tab === 'couples') return quote.policyType === 'Couples Discount';
  return quote.policyType === 'Single Life' || quote.policyType === 'Standard';
}

function parseDurationYearsAsNumber(value?: string): number {
  const result = parseBenefitPeriod(value);
  return result === 'lifetime' ? Infinity : result;
}

function inRange(value: number, min: number, max: number, sliderMax?: number): boolean {
  if (value === Infinity) return sliderMax !== undefined && max >= sliderMax;
  return value >= min && value <= max;
}

function getInflationProtection(quote: Quote) {
  const hasSingle = !!quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None';

  if (hasSingle) {
    const rate = parseInflationPercent(quote.inflationBenefitOption);
    return { hasSingle: true, single: rate, aob: rate, cob: rate };
  }

  return {
    hasSingle: false,
    single: undefined as number | undefined,
    aob: parseInflationPercent(quote.aobInflation),
    cob: parseInflationPercent(quote.cobInflation),
  };
}

function isWildcardInflationRange(range: number[]): boolean {
  return range[0] === 0 && range[1] >= 100;
}

function matchesInflationProtection(quote: Quote, filter: CombinedFilterType): boolean {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;
    const aobSet = !isWildcardInflationRange(filter.aob);
    const cobSet = !isWildcardInflationRange(filter.cob);

    if (hasSingle && typeof single === 'number') {
      // Single-inflation quotes (Nationwide/Securian) don't have separate AOB/COB data.
      // AOB-only filter: match their single rate against the AOB range.
      // COB-only or both set: exclude them — the user wants products with separate rates.
      if (aobSet && !cobSet) return inRange(single, minAob, maxAob);
      return false;
    }

    return inRange(aob, minAob, maxAob) && inRange(cob, minCob, maxCob);
  }

  const [minCombined, maxCombined] = filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, minCombined, maxCombined);
  }

  const isHybrid = aob !== cob;

  if (isHybrid) {
    return inRange(aob, minCombined, maxCombined) || inRange(cob, minCombined, maxCombined);
  }

  return inRange(aob, minCombined, maxCombined);
}

function getBenefitsDuration(quote: Quote) {
  const aobYears = parseDurationYearsAsNumber(quote.aobDuration);
  const cobYears = parseDurationYearsAsNumber(quote.cobDuration);

  if (aobYears || cobYears) {
    const totalYears =
      aobYears === Infinity || cobYears === Infinity ? Infinity : aobYears + cobYears;
    return { aobYears, cobYears, totalYears };
  }

  const totalYears = (quote.ltcBenefitDurationMonths ?? 0) / 12;
  return { aobYears: 0, cobYears: 0, totalYears };
}

function isWildcardRange(range: number[]): boolean {
  return range[0] === 0 && !isFinite(range[1]);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType): boolean {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minAob, maxAob] = filter.aob;
    const [minCob, maxCob] = filter.cob;

    if (aobYears || cobYears) {
      return (
        inRange(aobYears, minAob, maxAob, MAX_DURATION_YEARS) &&
        inRange(cobYears, minCob, maxCob, MAX_DURATION_YEARS)
      );
    }

    // Quote only has total duration (no separate AOB/COB data — e.g. Nationwide, Securian).
    // A wildcard range [0, Infinity] means that dimension was left empty by the user.
    const aobSet = !isWildcardRange(filter.aob);
    const cobSet = !isWildcardRange(filter.cob);

    if (aobSet && !cobSet) {
      // AOB-only filter: total-duration quotes can match since their entire duration is one phase.
      return inRange(totalYears, minAob, maxAob, MAX_DURATION_YEARS);
    }

    // COB-only or both set: exclude quotes without actual AOB/COB data (only OneAmerica has these).
    return false;
  }

  const [minCombined, maxCombined] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minCombined, maxCombined, MAX_DURATION_YEARS);
  }

  return inRange(totalYears, minCombined, maxCombined, MAX_DURATION_YEARS);
}

function hasDurationData(quote: Quote): boolean {
  return !!(quote.aobDuration || quote.cobDuration || quote.ltcBenefitDurationMonths);
}

function toSortedPeriods(set: Set<string>): BenefitPeriod[] {
  return Array.from(set)
    .map(stringToBenefitPeriod)
    .sort((periodA, periodB) => {
      if (periodA === LIFETIME_PERIOD) return 1;
      if (periodB === LIFETIME_PERIOD) return -1;
      return (periodA as number) - (periodB as number);
    });
}

function toSortedInflationOptions(map: Map<string, number>): InflationOption[] {
  return Array.from(map.entries())
    .sort(([, rateA], [, rateB]) => rateA - rateB)
    .map(([label, rate]) => ({ label, rate }));
}

function extractAvailableOptions(tabFilteredQuotes: Quote[]): BenefitDesignAvailableOptions {
  const durationSet = new Set<string>();
  const aobDurationSet = new Set<string>();
  const cobDurationSet = new Set<string>();
  const inflationMap = new Map<string, number>();
  const aobInflationMap = new Map<string, number>();
  const cobInflationMap = new Map<string, number>();

  for (const quote of tabFilteredQuotes) {
    if (hasDurationData(quote)) {
      const period = getBenefitPeriodFromQuote(quote);
      if (period === LIFETIME_PERIOD || (typeof period === 'number' && period > 0)) {
        durationSet.add(benefitPeriodToString(period));
      }
      const aob = parseDurationYearsAsNumber(quote.aobDuration);
      const cob = parseDurationYearsAsNumber(quote.cobDuration);
      if (aob > 0 || aob === Infinity)
        aobDurationSet.add(benefitPeriodToString(aob === Infinity ? LIFETIME_PERIOD : aob));
      if (cob > 0 || cob === Infinity)
        cobDurationSet.add(benefitPeriodToString(cob === Infinity ? LIFETIME_PERIOD : cob));
      if (!quote.aobDuration && !quote.cobDuration && quote.ltcBenefitDurationMonths) {
        const str = benefitPeriodToString(period);
        aobDurationSet.add(str);
        cobDurationSet.add(str);
      }
    }

    const label = getInitialInflationLabel(quote);
    if (label && label !== 'None') {
      const rate = parseInflationPercent(label);
      inflationMap.set(label, rate);
    }
    if (quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None') {
      const rate = parseInflationPercent(quote.inflationBenefitOption);
      aobInflationMap.set(quote.inflationBenefitOption, rate);
      cobInflationMap.set(quote.inflationBenefitOption, rate);
    } else {
      if (quote.aobInflation && quote.aobInflation !== 'None') {
        aobInflationMap.set(quote.aobInflation, parseInflationPercent(quote.aobInflation));
        inflationMap.set(quote.aobInflation, parseInflationPercent(quote.aobInflation));
      }
      if (quote.cobInflation && quote.cobInflation !== 'None') {
        cobInflationMap.set(quote.cobInflation, parseInflationPercent(quote.cobInflation));
        inflationMap.set(quote.cobInflation, parseInflationPercent(quote.cobInflation));
      }
    }
  }

  return {
    durationOptions: toSortedPeriods(durationSet),
    aobDurationOptions: toSortedPeriods(aobDurationSet),
    cobDurationOptions: toSortedPeriods(cobDurationSet),
    inflationOptions: toSortedInflationOptions(inflationMap),
    aobInflationOptions: toSortedInflationOptions(aobInflationMap),
    cobInflationOptions: toSortedInflationOptions(cobInflationMap),
  };
}

export function useBenefitDesignQuotes({
  originalQuotes,
  client,
  selectedTab,
  inputs,
  returnOnInvestmentCalculationType,
}: UseBenefitDesignQuotesParams): UseBenefitDesignQuotesResult {
  const tabFiltered = useMemo(() => {
    if (!originalQuotes?.length) return [];
    return originalQuotes.filter(quote => matchesTab(quote, selectedTab));
  }, [originalQuotes, selectedTab]);

  const availableOptions = useMemo(() => extractAvailableOptions(tabFiltered), [tabFiltered]);

  const quoteGroups = useMemo(() => {
    if (!tabFiltered.length || !client) return [];

    const { monthlyBenefit, benefitDuration, inflationProtection } = inputs;

    const filtered = tabFiltered.filter(
      quote =>
        (!benefitDuration || matchesBenefitsDuration(quote, benefitDuration)) &&
        (!inflationProtection || matchesInflationProtection(quote, inflationProtection)),
    );

    if (!filtered.length) return [];

    // When no monthly benefit is specified, show all quotes as-is
    if (monthlyBenefit === null) {
      const withROI = enrichQuotesWithROI(
        filtered,
        client,
        returnOnInvestmentCalculationType,
        { includeOriginalROIValues: true },
      );
      return groupQuotesForBenefitDesign(withROI);
    }

    // Reverse interpolate: for each quote, compute the premium required to deliver
    // the target monthly benefit. Quotes where the required premium is out of bounds are excluded.
    const interpolated = reverseInterpolateQuotes(filtered, monthlyBenefit);
    if (!interpolated.length) return [];

    const withROI = enrichQuotesWithROI(
      interpolated,
      client,
      returnOnInvestmentCalculationType,
      { includeOriginalROIValues: true },
    );
    return groupQuotesForBenefitDesign(withROI);
  }, [
    tabFiltered,
    client,
    inputs.monthlyBenefit,
    inputs.benefitDuration,
    inputs.inflationProtection,
    returnOnInvestmentCalculationType,
  ]);

  return { quoteGroups, availableOptions };
}
